import React, { useState, useEffect } from "react";
import { isValidName } from "@/utils";

import { useNavigate } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { genderOptions, isChild, GenderType } from "@/types/member";
import { membersService } from "@/services/members.service";
import {
  IconCalendarWeekFilled,
  IconCaretDownFilled,
} from "@tabler/icons-react";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import MRadio from "@/components/ui/atoms/MRadio";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MDialog from "@/components/ui/atoms/MDialog";
import { RelationshipType } from "@/types/relationship";
import MSpacer from "@/components/ui/atoms/MSpacer";

import { useMemberPermission } from "@/hooks/useMemberPermission";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const AddMemberDetails: React.FC = () => {
  const navigate = useNavigate();

  const memberInfo = getMemberInfo(true);

  const [genderDrawerOpen, setGenderDrawerOpen] = useState(false);
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [showDetailsFields] = useState(true);

  // Load names from add_member_form localStorage
  const [middleName] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).middleName || "" : "";
  });

  // Spouse check logic
  const { fetchSpouse } = useMemberPermission(
    "",
    memberInfo.current_family || memberInfo.familyId || ""
  );
  const firstName = (() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).firstName || "" : "";
  })();
  const lastName = (() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).lastName || "" : "";
  })();
  const nickname = (() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).nickname || "" : "";
  })();
  // No duplicate nickname state below
  const [dobMonth, setDobMonth] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).dobMonth || "" : "";
  });
  const [dobYear, setDobYear] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).dobYear || "" : "";
  });
  const [gender, setGender] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).gender || "" : "";
  });
  const [specialNeeds, setSpecialNeeds] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved && JSON.parse(saved).other_attrs
      ? JSON.parse(saved).other_attrs.specialNeeds || false
      : false;
  });

  // Helper to display DOB in 'MMM yyyy' format
  const dobDisplay =
    dobMonth && dobYear
      ? (() => {
          const date = new Date(Number(dobYear), Number(dobMonth) - 1);
          return date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        })()
      : "";

  // Auto-populate gender based on relationship
  useEffect(() => {
    const saved = localStorage.getItem("add_member_form");
    if (saved) {
      const form = JSON.parse(saved);
      const relationship = form.relationship;

      // Only set gender if it's not already set
      if (relationship && (!gender || gender === "")) {
        let autoGender;

        // Map relationships to appropriate genders
        switch (Number(relationship)) {
          case RelationshipType.FATHER:
          case RelationshipType.SON:
          case RelationshipType.BROTHER:
            autoGender = GenderType.MALE;
            break;
          case RelationshipType.MOTHER:
          case RelationshipType.DAUGHTER:
          case RelationshipType.SISTER:
            autoGender = GenderType.FEMALE;
            break;
          // For relationships that don't have a clear gender, leave as is
          default:
            autoGender = gender;
        }

        if (autoGender && autoGender !== gender) {
          setGender(autoGender);
          saveToLocalStorage({ gender: autoGender });
        }
      }
    }
  }, []);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_MEMBER_BASIC_INFO,
    });
  }, [memberInfo?.id]);

  const firstNameValid = isValidName(firstName);
  const middleNameValid = !middleName || isValidName(middleName);
  const lastNameValid = isValidName(lastName);
  const allFilled =
    firstName &&
    lastName &&
    dobMonth &&
    dobYear &&
    gender &&
    firstNameValid &&
    lastNameValid &&
    middleNameValid;

  const saveToLocalStorage = (data: any) => {
    const saved = localStorage.getItem("add_member_form");
    const form = saved ? JSON.parse(saved) : {};
    // Always preserve relationship if present
    if (form.relationship && !("relationship" in data)) {
      data.relationship = form.relationship;
    }
    localStorage.setItem(
      "add_member_form",
      JSON.stringify({ ...form, ...data })
    );
  };

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  const handleNext = async (skipDuplicateCheck = false) => {
    if (showDetailsFields) {
      saveToLocalStorage({
        firstName,
        middleName,
        lastName,
        dobMonth,
        dobYear,
        gender,
        nickname,
        other_attrs: { specialNeeds, docUploaded: false },
      });

      // Simple duplicate check (skip if user chose to continue anyway)
      if (!skipDuplicateCheck) {
        const familyMembers = await membersService.getManagedMembers();

        const duplicate = familyMembers.find(
          (member) =>
            member.first_name?.toLowerCase() === firstName.toLowerCase() &&
            member.last_name?.toLowerCase() === lastName.toLowerCase() &&
            member.dob_month === Number(dobMonth) &&
            member.dob_year === Number(dobYear)
        );

        if (duplicate) {
          setDuplicateDialogOpen(true);
          return;
        }
      }

      if (
        isChild({
          dob_year: Number(dobYear),
          dob_month: Number(dobMonth),
          dob_day: 1,
        })
      ) {
        let relationship;
        const savedForm = localStorage.getItem("add_member_form");
        if (savedForm) {
          const parsed = JSON.parse(savedForm);
          relationship = parsed.relationship;
        }
        // If SON or DAUGHTER, check for spouse before proceeding to consent
        if (
          relationship === RelationshipType.SON ||
          relationship === RelationshipType.DAUGHTER
        ) {
          const spouse = await fetchSpouse();
          if (spouse && spouse.id) {
            navigate("/profile/family/add-member/form/details/spouse", {
              state: { spouse },
            });
            return;
          }
        }
        // For child members, navigate to a direct consent page (not through invite flow)
        navigate("/profile/family/add-member/form/details/consent", {
          state: { firstName, isChild: true },
        });
      } else {
        navigate("/profile/family/add-member/form/details/invite", {
          state: { firstName, nickname },
        });
      }
    }
  };

  const handleNextClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    await handleNext(false);
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title="Set up their profile"
        description="Add details to create their profile."
        learnMoreContent={
          <>
            We use these details to build a health profile and help you keep
            records organised across family members. Age and gender also help
            tailor reminders and insights, where relevant. <br />
            <br />
            Your data stays private and secure.
          </>
        }
      />
      <div className="flex-1">
        <MSpacer mt={34} />
        {showDetailsFields && (
          <div className="flex flex-col gap-[32px]">
            <MInputField
              label="What’s their month & year of birth?"
              showLabel
              placeholder="Not Answered"
              variant="Underlined"
              size="md"
              value={dobDisplay || ""}
              onClick={() => setDobPickerOpen(true)}
              readOnly
              icon={<IconCalendarWeekFilled />}
              showIcon
            />
            <MDatePicker
              open={dobPickerOpen}
              onClose={() => setDobPickerOpen(false)}
              onDone={(date) => {
                setDobMonth(date.getMonth() + 1);
                setDobYear(date.getFullYear());
                saveToLocalStorage({
                  dobMonth: date.getMonth() + 1,
                  dobYear: date.getFullYear(),
                });
                setDobPickerOpen(false);
              }}
              initialValue={
                dobMonth && dobYear
                  ? new Date(Number(dobYear), Number(dobMonth) - 1)
                  : new Date(2000, 0, 1)
              }
              title="Select month & year"
              hideDate
            />
            <MInputField
              label="What is their gender?"
              showLabel
              placeholder={
                genderOptions.find((opt) => opt.value === gender)?.label ||
                "Not Answered"
              }
              variant="Underlined"
              size="md"
              value={
                genderOptions.find((opt) => opt.value === gender)?.label || ""
              }
              onClick={() => setGenderDrawerOpen(true)}
              icon={<IconCaretDownFilled size={16} />}
              showIcon
              readOnly
            />
            <MDropDownDrawer
              open={genderDrawerOpen}
              onClose={() => setGenderDrawerOpen(false)}
              title="Select gender"
              items={genderOptions.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
              }))}
              variant="single"
              selectedValues={gender ? [String(gender)] : []}
              onSelect={(val) => {
                const selected = Array.isArray(val) ? val[0] : val;
                const genderNum = Number(selected);
                setGender(genderNum);
                saveToLocalStorage({ gender: genderNum });
                setGenderDrawerOpen(false);
              }}
            />
            <div style={{ marginBottom: 30 }}>
              <div className="body-base-bold" style={{ marginBottom: 8 }}>
                Do they have a disability or special need?
              </div>
              <div>
                <div style={{ display: "flex", gap: 16 }}>
                  <label
                    className="body-base"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "40%",
                    }}
                  >
                    <MRadio
                      name="specialNeeds"
                      size="sm"
                      checked={specialNeeds === false}
                      value="false"
                      onChange={() => {
                        setSpecialNeeds(false);
                        saveToLocalStorage({ specialNeeds: false });
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        position: "relative",
                        top: 10,
                        left: -10,
                      }}
                    >
                      No
                    </span>
                  </label>
                  <label
                    className="body-base"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "40%",
                    }}
                  >
                    <MRadio
                      name="specialNeeds"
                      size="sm"
                      checked={specialNeeds === true}
                      value="true"
                      onChange={() => {
                        setSpecialNeeds(true);
                        saveToLocalStorage({ specialNeeds: true });
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        position: "relative",
                        top: 10,
                        left: -10,
                      }}
                    >
                      Yes
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          disabled={!allFilled}
          onClick={handleNextClick}
          className="w-full"
        >
          Next
        </MButton>
      </div>
      <MDialog
        isOpen={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        title="This profile may already exist."
        description="These details match an existing family member. Continuing may create a duplicate profile."
        primaryCtaText="Continue anyway"
        onPrimaryCta={() => {
          setDuplicateDialogOpen(false);
          // Continue with original flow, skipping duplicate check
          handleNext(true);
        }}
        secondaryCtaText="Cancel"
        onSecondaryCta={() => setDuplicateDialogOpen(false)}
      />
    </div>
  );
};

export default AddMemberDetails;
